import * as React from "react";
import { NavLink } from "react-router-dom";
import { getFirstAvailableSubMenuRoute } from "../../../../utils/navigationUtils";

const OnlineRegistrationSection = ({
  event,
  isParentMenuAvailable,
  getParentMenuSortOrder,
  getParentMenuDisplayNumber,
  isMenuItemAvailable,
  completed_modules,
  in_array,
  t,
  currentPath,
  menuHierarchy,
}) => {
  // State to store the modules from the same API that EventModuleOrder uses
  const [modulesFromAPI, setModulesFromAPI] = React.useState([]);
  const isFetchingRef = React.useRef(false);
  const lastFetchedEventIdRef = React.useRef(null);

  // Fetch modules from the same API that EventModuleOrder uses
  const fetchModulesFromAPI = React.useCallback(async () => {
    const eventId = event && event.id;
    if (!eventId) return;
    if (isFetchingRef.current) return;
    if (lastFetchedEventIdRef.current === eventId) return;
    
    try {
      isFetchingRef.current = true;
      // Import service dynamically to avoid circular dependency
      const { service } = await import("../../../../services/service");
      const response = await service.post(`${process.env.REACT_APP_URL}/event-settings/modules`, { event_id: eventId });
      
      if (response.success && response.data && response.data.modules) {
        setModulesFromAPI(response.data.modules);
        lastFetchedEventIdRef.current = eventId;
      }
    } catch (error) {
      // Silent error handling
    } finally {
      isFetchingRef.current = false;
    }
  }, [event && event.id]);

  // Fetch modules when component mounts or when event changes
  React.useEffect(() => {
    if (event && event.id) {
      fetchModulesFromAPI();
    }
  }, [event && event.id, fetchModulesFromAPI]);

  if (!isParentMenuAvailable("online_registration_form")) return null;
  if (!(Number(event.is_registration) === 1 || window.location.pathname.includes("/event/create"))) return null;

  // Get the first available sub-menu route for online_registration_form
  const firstAvailableRoute = getFirstAvailableSubMenuRoute("online_registration_form", menuHierarchy);

  // Get children for this parent from menuHierarchy, sorted by sort_order
  const onlineRegNode = Array.isArray(menuHierarchy)
    ? menuHierarchy.find((item) => item.alias === "online_registration_form")
    : null;

  const sortedChildren =
    onlineRegNode && Array.isArray(onlineRegNode.children)
      ? [...onlineRegNode.children]
          .filter((child) => isMenuItemAvailable(child.alias))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      : [];

  // Find the subregistration module from API or fallback to event.modules
  const modulesToUse = modulesFromAPI.length > 0 ? modulesFromAPI : event.modules;
  const subRegistrationModule = modulesToUse ? modulesToUse.find(module => module.alias === "subregistration") : null;

  // Map a child alias to the appropriate NavLink
  const renderChildItem = (child) => {
    switch (child.alias) {
      case "attendee_fields":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/basic-detail-form">
              <span className="active">{t("M_ATTENDEE_FIELDS")}</span>
            </NavLink>
          </li>
        );
      case "private_address_fields":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/private-address-detail-form">
              <span className="active">{t("M_PRIVATE_ADDRESS_FIELDS")}</span>
            </NavLink>
          </li>
        );
      case "attendee_types":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/attendee-type-form">
              <span>{t("M_ATTENDEE_TYPES")}</span>
            </NavLink>
          </li>
        );
      case "custom_fields":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/custom-fields">
              <span>{t("CUSTOM_FIELD_LISTING")}</span>
            </NavLink>
          </li>
        );
      case "company_fields":
        // Only show when payment_type === 1 (existing behaviour)
        if (
          !(event && event.eventsite_setting && Number(event.eventsite_setting.payment_type) === 1)
        ) {
          return null;
        }
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/company-detail-form">
              <span>{t("M_COMPANY_FIELDS")}</span>
            </NavLink>
          </li>
        );
      case "sub_registrations":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/sub-registration">
              <span>
                {subRegistrationModule ? subRegistrationModule.value : t("M_SUB_REGISTRATION")}
              </span>
            </NavLink>
          </li>
        );
      case "accommodation":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/manage/hotels">
              <span>{t("M_ACCOMODATION")}</span>
            </NavLink>
          </li>
        );
      case "gdpr_policy":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/gdpr">
              <span>{t("M_GDPR_POLICY")}</span>
            </NavLink>
          </li>
        );
      case "terms_conditions":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/tos">
              <span>{t("M_TERMS_CONDITIONS")}</span>
            </NavLink>
          </li>
        );
      case "attendee_gdpr_logs":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/attendee-gdpr-log">
              <span>{t("ATTENDEE_GDPR_LOG")}</span>
            </NavLink>
          </li>
        );
      case "add_attendee_to_registration":
        return (
          <li key={child.id}>
            <NavLink to="/event/registration/add-attendee-to-registration">
              <span>{t("M_ADD_ATTENDEE_TO_REGISTRATION")}</span>
            </NavLink>
          </li>
        );
      default:
        return null;
    }
  };

  return (
    <li style={{ order: getParentMenuSortOrder("online_registration_form"), counterSet: `section ${getParentMenuDisplayNumber("online_registration_form")}` }} className={`${window.location.pathname.includes("event/registration") ? "bold activeList" : ""} ${in_array("sub-registration", completed_modules) && in_array("hotel", completed_modules) && in_array("disclaimer", completed_modules) && in_array("additional-info", completed_modules) && in_array("additional-info", completed_modules) && in_array("general-info", completed_modules) ? "completed" : ""}`}>
      <span>
        <NavLink to={firstAvailableRoute || "/event/registration/basic-detail-form"}>{t("M_REGISTRATION_FORM_FIELDS")}</NavLink>
      </span>
      <ul>
        {sortedChildren.map((child) => renderChildItem(child)).filter(Boolean)}
      </ul>
    </li>
  );
};

export default OnlineRegistrationSection;
